import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Delegati, InfoUser } from '../models/content.model';
import { IdToken, LoginResponse } from '../models/login-response.model';
import { User } from '../models/user.model';
import { AuthenticationEndpointService } from './authentication-endpoint.service';
import { ConfigurationService } from './configuration.service';
import { DBkeys } from './db-keys';
import { JwtHelper } from './jwt-helper';
import { LocalStoreManager } from './local-store-manager.service';
import { Utilities } from './utilities';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {

  constructor(
    private readonly endpointService: AuthenticationEndpointService,
    private readonly configurations: ConfigurationService,
    private router: Router,
    private readonly msalService: MsalService,
    private localStorage: LocalStoreManager) { }

  getSecureApiToken$(): Observable<LoginResponse> {
    return this.endpointService.getSecureApiToken$();
  }

  getDelegati$(): Observable<Array<Delegati>> {
    return this.endpointService.getDelegatiEndpoint$();
  }

  getInfoUser$(): Observable<InfoUser> {
    return this.endpointService.getInfoUserEndpoint$();
  }

  getDelegation$(deleganteId: string): Observable<User> {
    return this.endpointService.getDelegationEndpoint$(deleganteId).pipe(
      map(response => {
        return this.processDelegationResponse(response);
      })
    );
  }

  getLogoutDelega() {
    return this.endpointService.getLogoutDelegaEndpoint();
  }

  
  redirectLogoutUser() {
    this.router.navigate([this.loginUrl]);
  }
  
  logout() {
    this.msalService.logout();
  }
  
  logoutDelegation() {
    this.endpointService.getLogoutDelegaEndpoint().subscribe(() => {
      Utilities.processDelegationLogout();
    });
  }

  private processDelegationResponse(response: LoginResponse) {

    const accessToken = response.access_token;

    if (accessToken == null)
      throw new Error('Received Delegation accessToken was empty.');

    const idToken = response.identity_token;
    const refreshToken = response.refresh_token || this.refreshToken;
    const expiresIn = response.expires_in;

    const tokenExpiryDate = new Date();
    tokenExpiryDate.setSeconds(tokenExpiryDate.getSeconds() + expiresIn);

    const accessTokenExpiry = tokenExpiryDate;

    const jwtHelper = new JwtHelper();
    const decodedIdToken = <IdToken>jwtHelper.decodeToken(response.access_token);

    const user = new User(decodedIdToken.sub, decodedIdToken.email, decodedIdToken.name, decodedIdToken.surname, decodedIdToken.fullname, decodedIdToken.role, decodedIdToken.act, decodedIdToken.delegation_id);
    user.isEnabled = true;

    Utilities.saveDelegationUserDetails(user, accessToken, refreshToken, accessTokenExpiry);

    return user;
  }

  /* getters & setters */
  public get loginUrl() {
    return this.configurations.loginUrl;
  }

  get delegationUser(): User {
    const user = this.localStorage.getDataObject<User>(DBkeys.DELEGATION_USER);
    return user;
  }

  get refreshToken(): string {
    return this.localStorage.getData(DBkeys.REFRESH_TOKEN);
  }

  get isDelegationMode(): boolean {
    return this.delegationUser != null;
  }

  get currentUser(): User {
    const user = this.localStorage.getDataObject<User>(DBkeys.CURRENT_USER);

    return user;
  }
  
  userHasRole(role): boolean{
    if(!role || role.length==0) return false;    
    return this.currentUser.roles.filter(r=> r.toLowerCase() == role.toLowerCase()).length>0;
  }
}
